import { format } from 'date-fns';
import { ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { CredentialAssigneeOption, PrintedCredentialRow } from '@/types';
import { DeleteCredentialDialog } from './DeleteCredentialDialog';
import { EditCredentialDialog } from './EditCredentialDialog';

interface CredentialsTableProps {
  eventSlug: string;
  credentials: PrintedCredentialRow[];
  assignableOptions: CredentialAssigneeOption[];
  credentialTypes: string[];
}

export function CredentialsTable({
  eventSlug,
  credentials,
  assignableOptions,
  credentialTypes,
}: CredentialsTableProps) {
  if (credentials.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>No printed credentials yet.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[160px]">Card slug</TableHead>
            <TableHead className="w-[110px]">Type</TableHead>
            <TableHead>Assigned to</TableHead>
            <TableHead className="w-[180px]">Label</TableHead>
            <TableHead className="w-[140px]">Printed</TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {credentials.map((credential) => (
            <TableRow key={credential.id}>
              <TableCell>
                <a
                  href={`/${eventSlug}/id/${credential.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                >
                  <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                    {credential.slug}
                  </code>
                  <ExternalLink className="h-3 w-3" />
                </a>
              </TableCell>
              <TableCell>
                <Badge variant="outline">{credential.type}</Badge>
              </TableCell>
              <TableCell>
                {credential.assignee ? (
                  <div className="flex flex-col">
                    <span className="font-medium text-sm">
                      {credential.assignee.fullName}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {credential.assignee.type}
                      {credential.assignee.slug
                        ? ` · ${credential.assignee.slug}`
                        : ''}
                    </span>
                  </div>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    Unassigned
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                <span className="text-muted-foreground text-sm">
                  {credential.label || '-'}
                </span>
              </TableCell>
              <TableCell>
                <span className="text-muted-foreground text-sm">
                  {format(new Date(credential.printedAt), 'dd MMM yyyy')}
                </span>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <EditCredentialDialog
                    eventSlug={eventSlug}
                    credential={credential}
                    assignableOptions={assignableOptions}
                    credentialTypes={credentialTypes}
                  />
                  <DeleteCredentialDialog
                    eventSlug={eventSlug}
                    credentialId={credential.id}
                    credentialSlug={credential.slug}
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
